<template>
  <Teleport to="body">
    <Transition name="splash">
      <div v-if="isVisible" class="splash-screen">
        <div class="splash-content">
          <!-- Logo Animation -->
          <div class="logo-wrapper">
            <span class="logo-text neat">Neat</span><span class="logo-text off">Off</span>
            <!-- Glow centered behind the logo -->
            <div class="glow-effect"></div>
          </div>
          
          <!-- Slogan -->
          <div class="slogan-wrapper">
            <p class="slogan">Premium Vacation Management</p>
          </div>
        </div>
        
        <!-- Loading Progress Bar -->
        <div class="progress-container">
          <div class="progress-bar"></div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, onMounted } from 'vue'

const isVisible = ref(false)

onMounted(() => {
  // 세션당 한 번만 보여주기 위해 sessionStorage 사용
  const hasSeenSplash = sessionStorage.getItem('hasSeenSplash')
  
  if (hasSeenSplash) {
    return // 이미 본 경우 isVisible이 false이므로 아무것도 안 뜹니다.
  }

  // 아직 안 본 경우: 즉시 '봤음' 처리 후 화면 노출
  sessionStorage.setItem('hasSeenSplash', 'true')
  isVisible.value = true

  // 애니메이션 수명 주기 제어 (3초 후 닫힘)
  setTimeout(() => {
    isVisible.value = false
  }, 3000) 
})
</script>

<style scoped>
.splash-screen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #ffffff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 100000;
  overflow: hidden;
}

[data-theme="dark"] .splash-screen {
  background-color: #1c1c1f;
}

.splash-content {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
}

.logo-wrapper {
  position: relative; /* For glow effect */
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-size: 65px;
  font-weight: 800;
  letter-spacing: -3px;
  margin-left: -3px; /* Optical correction for negative letter-spacing */
  animation: logoPop 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) both;
}

.logo-text {
  font-size: 65px !important;
  font-weight: 800;
  line-height: 1;
}

.logo-text.neat {
  color: var(--color-primary);
}

.logo-text.off {
  color: var(--color-text-muted);
}

[data-theme="dark"] .logo-text.off {
  color: var(--color-text-secondary);
}

.glow-effect {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 280px;
  height: 280px;
  background: radial-gradient(circle, var(--color-primary-soft) 0%, transparent 70%);
  z-index: -1;
  animation: pulse 2s infinite ease-in-out;
}

.slogan-wrapper {
  opacity: 0;
  animation: fadeInUp 1.0s cubic-bezier(0.22, 1, 0.36, 1) 0.1s both;
}

.slogan {
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text-muted);
  letter-spacing: 0.12em;
  text-transform: uppercase;
  margin: 0;
  text-align: center;
}

/* Progress Bar */
.progress-container {
  position: absolute;
  bottom: 60px;
  width: 140px;
  height: 3px;
  background-color: var(--color-bg-soft);
  border-radius: 10px;
  overflow: hidden;
}

[data-theme="dark"] .progress-container {
  background-color: var(--color-border);
}

.progress-bar {
  width: 0%;
  height: 100%;
  background-color: var(--color-primary);
  animation: progressFill 13s cubic-bezier(0.65, 0, 0.35, 1) forwards;
}

/* Animations */
@keyframes logoPop {
  0% { transform: scale(0.8); opacity: 0; }
  100% { transform: scale(1); opacity: 1; }
}

@keyframes fadeInUp {
  from { transform: translateY(10px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

@keyframes pulse {
  0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 0.5; }
  50% { transform: translate(-50%, -50%) scale(1.2); opacity: 0.8; }
}

@keyframes progressFill {
  0% { width: 0%; }
  100% { width: 100%; }
}

/* Transition (Clean Premium Fade) */
.splash-leave-active {
  transition: 
    opacity 3s cubic-bezier(0.4, 0, 0.2, 1),
    transform 3s cubic-bezier(0.4, 0, 0.2, 1);
}

.splash-leave-to {
  opacity: 0;
  transform: scale(0.98); /* 선명하게 뒤로 물러나며 사라짐 */
}
</style>
